import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import type { CreateIssueData } from "@/types/issues";

interface NewIssueFormProps {
  medicalTextExampleId: string;
  onSubmit: (data: CreateIssueData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function NewIssueForm({ medicalTextExampleId, onSubmit, onCancel, isSubmitting = false }: NewIssueFormProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmittingLocally, setIsSubmittingLocally] = useState(false);

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!title.trim() || isSubmittingLocally) {
      return;
    }

    setIsSubmittingLocally(true);

    try {
      await onSubmit({
        medical_text_example_id: medicalTextExampleId,
        title: title.trim(),
        description: description.trim() || undefined,
      });

      // Reset form only after successful submission
      setTitle("");
      setDescription("");
    } catch (error) {
      console.error("Error creating issue:", error);
    } finally {
      setIsSubmittingLocally(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      if (title.trim() && !isSubmittingLocally) {
        handleSubmit(e as any);
      }
    }
  };

  const isLoading = isSubmitting || isSubmittingLocally;

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">Create New Issue</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isLoading}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-4" onKeyDown={handleKeyDown}>
        <div>
          <Label htmlFor="issue-title">Title *</Label>
          <Input
            id="issue-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter issue title..."
            className="mt-1"
            disabled={isLoading}
          />
        </div>

        <div>
          <Label htmlFor="issue-description">Description</Label>
          <Textarea
            id="issue-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue in detail..."
            className="mt-1 min-h-[80px]"
            disabled={isLoading}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!title.trim() || isLoading}>
            <Plus className="w-4 h-4 mr-2" />
            {isLoading ? "Creating..." : "Create Issue"}
          </Button>
        </div>

        <div className="text-xs text-gray-500">Tip: Press Ctrl+Enter (Cmd+Enter on Mac) to submit</div>
      </div>
    </div>
  );
}
